import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { api } from '../services/api';
import { generateMockParcels, generateMockAssemblages } from '../services/mockData';

const ParcelContext = createContext();

const initialState = {
  parcels: [],
  filteredParcels: [],
  assemblages: [],
  favorites: [],
  selectedParcel: null,
  filters: {
    state: '',
    county: '',
    futureLandUse: '',
    futureLandUseMulti: [], // Array for multiple FLU selections (assemblage filters)
    inPlaceZoning: '',
    minAcreage: '',
    maxAcreage: '',
    maxAssessedValuePerAcre: '',
    filterType: 'single', // 'single' or 'multi'
    filterMode: 'futureLandUse' // 'futureLandUse' or 'inPlaceZoning'
  },
  loading: false,
  loadingParcelDetails: false,
  error: null,
  states: [],
  counties: [],
  futureLandUseOptions: [],
  inPlaceZoningOptions: [],
  searchSummary: null,
  totalCount: 0,
  tilesetUrl: '',
  // New fields for query runtime exceeded handling
  queryRuntimeExceeded: null,
  pendingSearchWithBbox: null,
};

const parcelReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    
    case 'SET_LOADING_PARCEL_DETAILS':
      return { ...state, loadingParcelDetails: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    case 'SET_PARCELS':
      return { ...state, parcels: action.payload };
    
    case 'SET_FILTERED_PARCELS':
      return { ...state, filteredParcels: action.payload };
    
    case 'SET_ASSEMBLAGES':
      return { ...state, assemblages: action.payload };
    
    case 'SET_SELECTED_PARCEL':
      return { ...state, selectedParcel: action.payload };
    
    case 'SET_FILTERS':
      return { ...state, filters: { ...state.filters, ...action.payload } };
    
    case 'SET_STATES':
      return { ...state, states: action.payload };
    
    case 'SET_COUNTIES':
      return { ...state, counties: action.payload };
    
    case 'SET_FUTURE_LAND_USE_OPTIONS':
      return { ...state, futureLandUseOptions: action.payload };
    
    case 'SET_IN_PLACE_ZONING_OPTIONS':
      return { ...state, inPlaceZoningOptions: action.payload };
    
    case 'SET_SEARCH_SUMMARY':
      return { ...state, searchSummary: action.payload };
    
    case 'SET_TOTAL_COUNT':
      return { ...state, totalCount: action.payload };
    
    case 'SET_TILESET_URL':
      return { ...state, tilesetUrl: action.payload };
    
    case 'SET_QUERY_RUNTIME_EXCEEDED':
      return { ...state, queryRuntimeExceeded: action.payload };
    
    case 'SET_PENDING_SEARCH_WITH_BBOX':
      return { ...state, pendingSearchWithBbox: action.payload };
    
    case 'ADD_TO_FAVORITES':
      return { 
        ...state, 
        favorites: [...state.favorites, action.payload]
      };
    
    case 'REMOVE_FROM_FAVORITES':
      return { 
        ...state, 
        favorites: state.favorites.filter(item => item.id !== action.payload)
      };
    
    case 'CLEAR_FAVORITES':
      return { ...state, favorites: [] };
    
    case 'SET_FAVORITES':
      return { ...state, favorites: action.payload };
    
    default:
      return state;
  }
};

export const ParcelProvider = ({ children }) => {
  const [state, dispatch] = useReducer(parcelReducer, initialState);



  // Load states on component mount
  useEffect(() => {
    loadStates();
  }, []);

  // Load counties when state changes
  useEffect(() => {
    if (state.filters.state) {
      loadCounties(state.filters.state);
    }
  }, [state.filters.state]);

  // Load land use options when county changes
  useEffect(() => {
    if (state.filters.county) {
      loadLandUseOptions();
    }
  }, [state.filters.county]);

  useEffect(() => {
    if (state.favorites.length === 0) {
      // Add 2 mock parcels and 1 mock assemblage as favorites
      const mockParcels = generateMockParcels({}).slice(0, 2).map(p => ({ ...p, type: 'parcel', id: p.id }));
      const mockAssemblage = generateMockAssemblages({}).slice(0, 1).map(a => ({ ...a, type: 'assemblage', id: a.id }));
      dispatch({ type: 'SET_FAVORITES', payload: [...mockParcels, ...mockAssemblage] });
    }
  }, []);

  const loadStates = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const states = await api.getStates();
      dispatch({ type: 'SET_STATES', payload: states });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load states' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const loadCounties = async (state) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const counties = await api.getCountiesByState(state);
      dispatch({ type: 'SET_COUNTIES', payload: counties });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load counties' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const loadLandUseOptions = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const [fluOptions, zoningOptions] = await Promise.all([
        api.getFutureLandUse(state.filters.state, state.filters.county),
        api.getInPlaceZoning(state.filters.state, state.filters.county)
      ]);
      
      dispatch({ type: 'SET_FUTURE_LAND_USE_OPTIONS', payload: fluOptions });
      dispatch({ type: 'SET_IN_PLACE_ZONING_OPTIONS', payload: zoningOptions });
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load land use options' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const searchParcels = async (bbox = null) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_QUERY_RUNTIME_EXCEEDED', payload: null });
      
      const response = await api.searchParcels({
        ...state.filters,
        includeAssemblages: state.filters.filterType === 'multi',
        bbox: bbox
      });
      
      // Transform parcel data to match expected format
      const transformedParcels = response.parcels.map(parcel => ({
        parcel_id: parcel.parcel_id,
        land_acres: Math.round(parseFloat(parcel.land_acres) * 100) / 100,
        land_use_category: parcel.land_use_category,
        zoning: parcel.zoning,
        addr: parcel.addr,
        owner: parcel.own1_last + ' ' + parcel.own1_first,
        assd_imp: Math.round(parseFloat(parcel.assd_imp) * 100) / 100,
        geometry: parcel.geometry,
        center: parcel.center
      }));

      // Transform assemblage data - now the assemblages come in the 'parcels' array with different structure
      const transformedAssemblages = response.parcels ? response.parcels.map(assemblage => ({
        cluster_id: assemblage.cluster_id || `assemblage-${Math.random()}`,
        parcel_ids: Array.isArray(assemblage.parcel_ids) ? assemblage.parcel_ids : [],
        total_acres: Math.round(parseFloat(assemblage.total_acres) * 100) / 100 || 0,
        numberOfParcels: assemblage.chunk_size || 0,
        land_use_category: 'Assemblage', // Placeholder - not in API response
        zoning: 'Assemblage', // Placeholder - not in API response
        addr: 'Assemblage',
        owner: 'Assemblage',
        assd_imp: Math.round(parseFloat(assemblage.total_imp_val) * 100) / 100 || 0,
        geometry: assemblage.geometry,
        center: assemblage.center
      })) : [];
      
      // Update search summary and total count
      dispatch({ type: 'SET_SEARCH_SUMMARY', payload: response.filter_summary });
      dispatch({ type: 'SET_TOTAL_COUNT', payload: response.total_count });
      
      if (state.filters.filterType === 'single') {
        dispatch({ type: 'SET_FILTERED_PARCELS', payload: transformedParcels });
        dispatch({ type: 'SET_ASSEMBLAGES', payload: [] });
      } else {
        dispatch({ type: 'SET_FILTERED_PARCELS', payload: [] });
        dispatch({ type: 'SET_ASSEMBLAGES', payload: transformedAssemblages });
      }
    } catch (error) {
      console.error('Search error:', error);
      
      // Check if this is a query runtime exceeded error for multi-parcel queries
      if (error.message && error.message.includes('Query runtime exceeded') && state.filters.filterType === 'multi') {
        // Parse the error to extract suggestions
        let suggestions = [];
        try {
          const errorMatch = error.message.match(/"suggestions":\s*\[(.*?)\]/s);
          if (errorMatch) {
            suggestions = JSON.parse(`[${errorMatch[1]}]`);
          }
        } catch (parseError) {
          console.warn('Could not parse error suggestions:', parseError);
        }
        
        dispatch({ 
          type: 'SET_QUERY_RUNTIME_EXCEEDED', 
          payload: {
            message: error.message,
            suggestions: suggestions
          }
        });
      } else {
        dispatch({ type: 'SET_ERROR', payload: 'Failed to search parcels' });
      }
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Function to retry search with current map bounds
  const retrySearchWithBbox = async (bbox) => {
    dispatch({ type: 'SET_PENDING_SEARCH_WITH_BBOX', payload: bbox });
    await searchParcels(bbox);
    dispatch({ type: 'SET_PENDING_SEARCH_WITH_BBOX', payload: null });
  };

  // Function to cancel the query runtime exceeded modal
  const cancelQueryRuntimeExceeded = () => {
    dispatch({ type: 'SET_QUERY_RUNTIME_EXCEEDED', payload: null });
  };

  const addToFavorites = (item) => {
    dispatch({ type: 'ADD_TO_FAVORITES', payload: item });
  };

  const removeFromFavorites = (id) => {
    dispatch({ type: 'REMOVE_FROM_FAVORITES', payload: id });
  };

  const clearFavorites = () => {
    dispatch({ type: 'CLEAR_FAVORITES' });
  };

  const updateFilters = (filters) => {
    dispatch({ type: 'SET_FILTERS', payload: filters });
  };

  const setSelectedParcel = (parcel) => {
    dispatch({ type: 'SET_SELECTED_PARCEL', payload: parcel });
  };

  const loadParcelDetails = async (parcelId) => {
    try {
      dispatch({ type: 'SET_LOADING_PARCEL_DETAILS', payload: true });
      
      // Get state and county from current filters
      const currentState = state.filters.state || 'ca';
      const county = state.filters.county || 'orange';
      
      const result = await api.getParcelDetails(parcelId, currentState, county);
      
      if (result.success && result.found && result.parcel) {
        // Set the detailed parcel information as selected
        const selectedParcelData = {
          ...result.parcel,
          type: 'parcel',
          parcel_id: result.parcel.parcel_id,
          land_acres: result.parcel.land_acres || result.parcel.total_acres || 0,
          land_use_category: result.parcel.land_use_category || 'N/A',
          zoning: result.parcel.zoning || 'N/A',
          addr: result.parcel.addr || 'N/A',
          owner: result.parcel.owner || 'N/A',
          assd_imp: result.parcel.assd_imp || 0
        };

        dispatch({ type: 'SET_SELECTED_PARCEL', payload: selectedParcelData });
        return selectedParcelData;
      } else {
        console.warn('No detailed parcel information found for:', parcelId);
        return null;
      }
    } catch (error) {
      console.error('Error loading parcel details:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load parcel details' });
      return null;
    } finally {
      dispatch({ type: 'SET_LOADING_PARCEL_DETAILS', payload: false });
    }
  };

  const setTilesetUrl = useCallback((url) => {
    dispatch({ type: 'SET_TILESET_URL', payload: url });
  }, []);

  const value = {
    ...state,
    searchParcels,
    retrySearchWithBbox,
    cancelQueryRuntimeExceeded,
    addToFavorites,
    removeFromFavorites,
    clearFavorites,
    updateFilters,
    setSelectedParcel,
    loadParcelDetails,
    loadStates,
    loadCounties,
    loadLandUseOptions,
    tilesetUrl: state.tilesetUrl,
    setTilesetUrl
  };

  return (
    <ParcelContext.Provider value={value}>
      {children}
    </ParcelContext.Provider>
  );
};

export const useParcelContext = () => {
  const context = useContext(ParcelContext);
  if (!context) {
    throw new Error('useParcelContext must be used within a ParcelProvider');
  }
  return context;
}; 